import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { faPause, faPlay } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import { Button } from '@components/main-components/button/Button';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AudioPlayerCollectModal } from '@components/modals/Collect/AudioPlayerCollectModal';
import { ModalType, useModal } from '@context/modal';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { webappUriPath } from '@utils/url';

export const PlayerController = () => {
  const { asPath } = useRouter();
  const { activeTrackId, playing } = useAudioController();
  const { releaseInfo: currentTrackData } = usePlayerTrackInfo({
    trackId: activeTrackId,
    shouldFetch: true,
  });
  const { openModal } = useModal();

  const releaseId = currentTrackData?.id;

  if (!currentTrackData) return null;
  const releasePath = webappUriPath(currentTrackData.webappUri);

  const showCollect = !asPath.startsWith(releasePath);

  return (
    <Container>
      {showCollect && !!releaseId && (
        <Button
          className="h-8 w-[65px] border border-neutral500 leading-normal focus:border-neutral500 hover:border-neutral500 active:border-neutral500"
          darkMode
          label="Collect"
          variant="tertiary"
          size="S"
          onClick={e => {
            e.stopPropagation();
            trackClickEvent({
              event: 'Collect Now Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent: 'Audio Player',
              },
            });
            openModal(ModalType.AUDIO_PLAYER_COLLECT, {
              body: <AudioPlayerCollectModal releaseId={releaseId} />,
            });
          }}
        />
      )}

      <IconButton
        variant="inverted"
        icon={<FontAwesomeIcon icon={playing ? faPause : faPlay} />}
        onClick={e => {
          // prevents the onClick on the main player to bubble to this button and allows
          // user to click this button without triggering a parent onClick
          e.stopPropagation();
          togglePlayPause();
        }}
        size={24}
        animate
      />
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});
